import { IconNames } from '../../common/components/Icon/IconNames';
import { ServiceCategory, ServiceOffer, ServicePartner } from '../types';
import logoBrostik from '../../assets/images/logo-brostik.png';
import logoEvlAuto from '../../assets/images/logo-evl-auto.svg';
import logoEvlFinance from '../../assets/images/logo-evl-fin.png';
import logoEvlFinanceDark from '../../assets/images/logo-evl-finance-dark.svg';
import logoEvlMark from '../../assets/images/logo-evl-mark.svg';
import logoEvlMarkLight from '../../assets/images/logo-evl-mark-light.svg';
import logoEvlRealityDark from '../../assets/images/logo-evl-reality-dark.svg';
import logoMrParkit from '../../assets/images/logo-mrparkit.svg';
import logoPureConcept from '../../assets/images/logo-pure-concept.webp';
import logoVanerka from '../../assets/images/logo-vanerka.png';
import logoVeolia from '../../assets/images/logo-veolia.svg';
import logoVexta from '../../assets/images/logo-vexta.svg';

/**
 * FE konfigurace katalogu (doc/services-catalog.md): BE drží metadata (co je aktivní, pro koho,
 * v jakém pořadí), FE drží vzhled a texty vázané na kategorii / slug partnera.
 * Slugy jsou neměnné – klíčují se jimi loga, ikony i souhlasy.
 */

/** Ikona kategorie (karty, chipy „Co řešíte?", detail). */
export const CATEGORY_ICONS: Record<ServiceCategory, IconNames> = {
  finance: 'shield',
  energy: 'lightning',
  real_estate: 'house',
  construction: 'building',
  elevators: 'elevator',
  windows: 'home',
  cleaning: 'broom',
  parking: 'car',
  legal: 'judgeHammer',
  other: 'star',
};

/**
 * Ikona konkrétní služby (klíč = slug nabídky) – kde ikona kategorie nestačí (u Financí by jinak
 * bylo všude štít, rozhodnutí Ondřeje 10/2026). Nabídka bez záznamu dostane ikonu své kategorie.
 */
export const OFFER_ICONS: Partial<Record<string, IconNames>> = {
  'building-insurance': 'shield',
  'building-loan': 'percent',
  'building-investment': 'trendingUp',
  'apartment-insurance': 'umbrella',
  'vehicle-insurance': 'car',
  'life-insurance': 'heartPulse',
  'pet-insurance': 'paw',
  mortgage: 'percent',
  'consumer-loan': 'wallet',
  'energy-audit': 'bulb',
  'energy-sources': 'lightning',
  'svj-real-estate': 'building',
  'apartment-sale': 'house',
  'apartment-valuation': 'calculator',
  'apartment-purchase': 'keys',
};

export const offerIcon = (offer: Pick<ServiceOffer, 'slug' | 'category'>): IconNames =>
  OFFER_ICONS[offer.slug] ?? CATEGORY_ICONS[offer.category];

/** Pořadí chipů „Co řešíte?" – jen kategorie, které katalog pro daného uživatele skutečně obsahuje. */
export const CATEGORY_ORDER: ServiceCategory[] = [
  'finance',
  'energy',
  'real_estate',
  'elevators',
  'construction',
  'windows',
  'cleaning',
  'parking',
  'legal',
  'other',
];

/**
 * Loga partnerů – klíč = slug. Partner bez loga dostane monogram z názvu (PartnerLogo).
 * Servis oken (samostatná firma, 10/2026 ještě neznámá) logo zatím nemá – doplní se po podpisu.
 */
export const PARTNER_LOGOS: Partial<Record<string, string>> = {
  'evlastnici-finance': logoEvlFinance,
  brostik: logoBrostik,
  'vytahy-vanerka': logoVanerka,
  vexta: logoVexta,
  mrparkit: logoMrParkit,
  'pure-concept': logoPureConcept,
};

/**
 * Loga divizí eVlastníci ve variantě pro tmavé pozadí (CI podklady na sdíleném disku
 * `I:\Sdílené disky\!eVlastníci Finance!\CI\loga`) – zobrazují se na tmavých kartách vlastních
 * služeb. Divize bez loga (Energetika) dostane ikonu oblasti + název.
 */
export const INTERNAL_LOGOS_DARK: Partial<Record<string, string>> = {
  'evlastnici-finance': logoEvlFinanceDark,
  'evlastnici-reality': logoEvlRealityDark,
};

/**
 * Vlastní služba poskytovaná v partnerství s jinou značkou: karta i detail ukáží „značka eVlastníci
 * (jen grafika) + logo partnera". Energetiku děláme s Veolií (rozhodnutí 10/2026) – Veolia proto
 * není samostatný partner v katalogu. Logo partnera se na tmavém pozadí vykreslí bíle (CSS filtr).
 */
export const INTERNAL_COBRAND_LOGOS: Partial<Record<string, { partnerLogo: string; partnerName: string }>> = {
  'evlastnici-energy': { partnerLogo: logoVeolia, partnerName: 'Veolia' },
};
/**
 * „Výhoda pro uživatele eVlastníků" u vlastních divizí – motivace, proč poptat zrovna u nás (Ondřej 10/2026):
 * Finance = podmínky a bonusy z objemu smluv, Reality = sleva 0,5 % z běžné realitní provize. Zobrazuje se
 * VLASTNÍKŮM na dlaždici divize a v detailu (karta partnera + hlavička formuláře). `figure` = velké číslo vlevo
 * (jen Reality), bez něj ikona. Texty tady, ne v DB – je to marketingový prvek FE, ne metadata partnera.
 */
export interface MemberBenefit {
  figure?: string;
  text: string;
  /** Jedna řádka do hlavičky formuláře. */
  short: string;
}

export const MEMBER_BENEFITS: Partial<Record<string, MemberBenefit>> = {
  'evlastnici-finance': {
    text: 'Sjednáváme stovky smluv ročně. Bonusy a podmínky, které u pojišťoven a bank vyjednáme díky objemu, přenášíme na vás – jednotlivec na ně nedosáhne.',
    short: 'Podmínky a bonusy z objemu stovek smluv – jen pro uživatele eVlastníků.',
  },
  'evlastnici-reality': {
    figure: '−0,5 %',
    text: 'Pro uživatele aplikace eVlastníci: sleva 0,5 % z běžné realitní provize při prodeji bytu přes eVlastníci Reality.',
    short: 'Sleva 0,5 % z realitní provize pro uživatele eVlastníků.',
  },
};

/** Značka eVlastníci (jen grafika): bílý klín pro tmavé pozadí, Blue800 klín pro světlé. */
export const EVL_MARK = { dark: logoEvlMark, light: logoEvlMarkLight };

/**
 * Aplikace eVlastníci Auto: v katalogu je jako interní partner (seed `evlastnici-auto`), ale místo
 * karty služby se vykresluje jako banner (`CarAppBanner`), aby mezi službami nezapadla; detail
 * nabídky slouží ke sběru zájemců o prémiovou verzi zdarma.
 */
export const CAR_APP_PARTNER_SLUG = 'evlastnici-auto';
export const CAR_APP_ICON = logoEvlAuto;

/**
 * Souhlas s předáním údajů (text k odsouhlasení právníkem – rozhodnutí 10/2026, zatím návrh):
 * - souhlas dává PBT Digital s.r.o. = provozovatel aplikace a správce údajů dle VOP/GDPR registrace
 *   (ne mateřská eVlastníci s.r.o.; koncepci předávání údajů ve skupině Ondřej s kolegou teprve připravuje),
 * - finanční služby: eVlastníci Finance & Reality s.r.o. (IČO 08914575) má zatím licenci jen na pojištění
 *   vozidel, ostatní finanční produkty sjednává spolupracující poradce přes Credomia s.r.o. (IČO 07622546)
 *   – souhlas proto jmenuje OBĚ společnosti, přes kterou poptávka půjde, se rozhoduje až podle produktu,
 * - realitní služby poskytuje eVlastníci Finance & Reality s.r.o.,
 * - ostatní interní služby zpracovává provozovatel aplikace,
 * - externí partner dostane údaje jen k vyřízení poptávky (dokud nemá nastavené přeposílání,
 *   zůstávají u nás – text to říká obecně, aby platil v obou režimech).
 */
export const consentText = (partner: ServicePartner): string => {
  if (partner.slug === 'evlastnici-finance') {
    return 'Souhlasím, aby společnost PBT Digital s.r.o., IČO 11716029 (provozovatel aplikace eVlastníci), předala mé údaje v rozsahu tohoto formuláře společnosti eVlastníci Finance & Reality s.r.o., IČO 08914575, a společnosti Credomia s.r.o., IČO 07622546, za účelem zpracování poptávky a kontaktování s nabídkou. Souhlas mohu kdykoli odvolat na pomoc@evlastnici.cz.';
  }

  if (partner.slug === 'evlastnici-reality') {
    return 'Souhlasím, aby společnost PBT Digital s.r.o., IČO 11716029 (provozovatel aplikace eVlastníci), předala mé údaje v rozsahu tohoto formuláře společnosti eVlastníci Finance & Reality s.r.o., IČO 08914575, za účelem zpracování poptávky a kontaktování s nabídkou. Souhlas mohu kdykoli odvolat na pomoc@evlastnici.cz.';
  }

  if (partner.kind === 'internal') {
    return 'Souhlasím, aby společnost PBT Digital s.r.o., IČO 11716029 (provozovatel aplikace eVlastníci), zpracovala mé údaje v rozsahu tohoto formuláře za účelem vyřízení poptávky a kontaktování s nabídkou. Souhlas mohu kdykoli odvolat na pomoc@evlastnici.cz.';
  }

  return `Souhlasím, aby společnost PBT Digital s.r.o., IČO 11716029 (provozovatel aplikace eVlastníci), předala mé údaje v rozsahu tohoto formuláře partnerovi ${partner.name} za účelem zpracování poptávky a kontaktování s nabídkou. Souhlas mohu kdykoli odvolat na pomoc@evlastnici.cz.`;
};

/** Volitelná pole s přílohou dávají smysl jen tam, kde je co posílat (smlouva k revizi, fotky stavu). */
export const OFFERS_WITH_ATTACHMENTS = new Set([
  'building-insurance',
  'apartment-insurance',
  'vehicle-insurance',
  'life-insurance',
  'building-renovation',
  'elevator-service',
  'window-service',
  'legal-audit',
  'energy-audit',
]);
